import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import AppEmpty from '@nxsys/components/AppEmpty';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  TimesheetApproveStatusEnum,
  TimesheetStatusEnum,
} from '@nxsys/constants/AppEnums';
import {
  useApproveTimesheet,
  useCancelTimesheet,
  useGetTimesheetGrouping,
} from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { timesheetsAction } from '@nxsys/store/slices/timesheets/reducer/timesheetsSlice';
import { GetListTimesheetsParams } from '@nxsys/store/slices/timesheets/type';
import { GetTimesheetGroupingParams } from '@nxsys/types/api';
import {
  TimeSheetDataItem,
  TimesheetGroupingDataItem,
} from '@nxsys/types/models/timesheet';
import { showMessage } from '@nxsys/utils/message';
import { Space } from 'antd';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import ChangeTaxPeriodModal from '../components/ChangeTaxPeriodModal';
import HeaderListing from '../components/HeaderListing';
import TimesheetGroupCard from '../components/TimesheetGroupCard';
import { resolveChangeTaxPeriodSelection } from './changeTaxPeriodSelection';
import { getPendingAgencyApprovalColumns } from './column';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type Props = {
  cbAfterAction?: () => void;
  isDashboard?: boolean;
};

const PendingApproval: React.FC<Props> = ({
  cbAfterAction = () => {},
  isDashboard = false,
}) => {
  const { messages, formatMessage } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const theme = useTheme();
  const { payrollCountry, isBureauEmployer, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [isGroupTimesheet, setIsGroupTimesheet] = useState(true);
  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);
  const [taxPeriodModal, setTaxPeriodModal] = useState<{
    isOpen: boolean;
    timesheets: TimeSheetDataItem[];
  }>({
    isOpen: false,
    timesheets: [],
  });

  const toggleTaxPeriodModal = () => {
    setTaxPeriodModal((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  };

  const {
    value: timesheetGrouping,
    loading: isTimesheetGroupingLoading,
    run: getTimesheetGrouping,
  } = useGetTimesheetGrouping();
  const timesheetGroupingData = useMemo(
    () => timesheetGrouping?.data,
    [timesheetGrouping],
  );

  const dispatch = useDispatch<AppDispatch>();
  const {
    loading: isGetTimesheetPending,
    timesheetsData: { list, pagination, queryParams },
  } = useSelector((state: AppState) => state.timesheetsStore);

  const { run: cancelTimesheet, loading: isCancelTimesheetPending } =
    useCancelTimesheet();

  const { run: approveTimesheet, loading: isApproveTimesheetPending } =
    useApproveTimesheet();

  useEffect(() => {
    handleGetTimesheetGrouping();
  }, []);

  const handleGetTimesheetGrouping = useCallback(
    (params?: GetTimesheetGroupingParams) => {
      const newParams = {
        ...(params ?? {}),
        approveStatus: TimesheetApproveStatusEnum.PENDING,
        companyRefs: resolveFilterCompanyIds(
          params?.companyRefs,
          isBureauEmployer,
          user?.companyUserIds,
        ),
      };
      getTimesheetGrouping(newParams);
    },
    [getTimesheetGrouping],
  );

  const getTimeSheetList = useCallback(
    (params: GetListTimesheetsParams) => {
      const newParams = {
        ...params,
        approveStatus: TimesheetApproveStatusEnum.PENDING,
      };
      dispatch(timesheetsAction.getListTimesheets(newParams));
    },
    [dispatch],
  );

  const handleGoBack = useCallback(() => {
    setIsGroupTimesheet(true);
    handleGetTimesheetGrouping();
  }, []);

  const refetchTimesheetList = () => {
    getTimeSheetList({ ...queryParams });
    cbAfterAction();
  };

  const handleSelectTimesheetGroup = useCallback(
    (data: TimesheetGroupingDataItem) => {
      setIsGroupTimesheet(false);
      getTimeSheetList({
        page: 0,
        size: 20,
        taxPeriod: data.taxPeriod,
        payFrequency: data.payFrequency,
      });
    },
    [],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    getTimeSheetList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleFilter = (params: GetListTimesheetsParams) => {
    getTimeSheetList({
      ...queryParams,
      ...params,
      page: 0,
    });
  };

  const handleCancel = useCallback((list: TimeSheetDataItem[]) => {
    const validItems = list.filter(
      (item) =>
        item?.approveStatus !== TimesheetStatusEnum.CANCEL &&
        !item?.newDraftPayroll &&
        !item?.newPayroll,
    );
    const ids = validItems.map((item) => item.id);
    if (ids.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['timesheet.noValidTimesheetToCancel'],
      });
      return;
    }
    AppConfirmationDelete.open({
      title: messages['timesheet.cancel'],
      paragraph: formatMessage(
        { id: 'timesheet.cancelConfirm' },
        {
          timesheetList: (
            <span style={{ color: theme.palette.blue[500] }}>
              {ids.join(', ')}
            </span>
          ),
        },
      ),
      onConfirm: async () => {
        await cancelTimesheet(ids);
        showMessage({
          type: 'success',
          content: messages['timesheet.cancelSuccess'],
        });
        refetchTimesheetList();
      },
    });
  }, []);

  const handleCancelSelected = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleCancel(selectedRows);
  }, [selectedRows]);

  const handleApprove = useCallback((list: TimeSheetDataItem[]) => {
    const ids = list.map((item) => item.id);
    approveTimesheet({ ids }).then(() => {
      showMessage({
        type: 'success',
        content: messages['timesheet.approveSuccess'],
      });
      refetchTimesheetList();
      setSelectedRows([]);
    });
  }, []);

  const handleApproveSelected = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleApprove(selectedRows);
  }, [selectedRows]);

  const handleOpenChangeTaxPeriod = useCallback((list: TimeSheetDataItem[]) => {
    const selection = resolveChangeTaxPeriodSelection(list);
    switch (selection.status) {
      case 'empty':
        showMessage({
          type: 'warning',
          content: messages['common.noSelected'],
        });
        return;
      case 'noneEligible':
        showMessage({
          type: 'warning',
          content: messages['timesheet.noValidTimesheetToChangeTaxPeriod'],
        });
        return;
      case 'mixedFrequency':
        showMessage({
          type: 'warning',
          content: messages['timesheet.changeTaxPeriodMixedFrequency'],
        });
        return;
      case 'ok':
        setTaxPeriodModal({ isOpen: true, timesheets: selection.selected });
        return;
    }
  }, []);

  const handleChangeTaxPeriodSelected = useCallback(() => {
    handleOpenChangeTaxPeriod(selectedRows);
  }, [selectedRows]);

  const renderTitle = useCallback(() => {
    return (
      <Space>
        {!isGroupTimesheet && <AppBackButton onClick={handleGoBack} />}
        <AppPageTitle
          prefix={
            isBureau
              ? messages['timesheet.pendingEmployerApproval']
              : messages['timesheet.pendingAgencyApproval']
          }
          separator=""
          title={''}
        />
      </Space>
    );
  }, [messages, isGroupTimesheet, isBureau]);

  const renderRightContent = useCallback(() => {
    return !isGroupTimesheet ? (
      <div className="flex items-center gap-x-2">
        <AppButton
          name={messages['timesheet.changeTaxPeriod']}
          variant="outlined"
          onClick={handleChangeTaxPeriodSelected}
        />
        <AppButton
          name={messages['timesheet.cancel']}
          variant="outlined"
          onClick={handleCancelSelected}
        />
        <AppButton
          name={messages['timesheet.approveTimesheet']}
          onClick={handleApproveSelected}
        />
      </div>
    ) : null;
  }, [
    messages,
    isGroupTimesheet,
    handleChangeTaxPeriodSelected,
    handleCancelSelected,
    handleApproveSelected,
  ]);

  const handleView = useCallback(
    (id: string) => {
      navigate(`/timesheets/${id}`, {
        query: { from: '/timesheets/pending' },
      });
    },
    [navigate],
  );

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.startDate ||
    !!queryParams?.endDate ||
    !!queryParams?.batchFrom ||
    !!queryParams?.batchTo ||
    !!queryParams?.timesheetId ||
    (queryParams?.agencyRefs && queryParams?.agencyRefs?.length > 0) ||
    (queryParams?.companyRefs && queryParams?.companyRefs?.length > 0) ||
    !!queryParams?.paymentModel;

  const renderTable = () => (
    <AppTableContainer
      rowKey="id"
      data={list ?? []}
      loading={isGetTimesheetPending}
      page={pagination.current}
      total={pagination.totalDocs}
      pageSize={pagination.pageSize}
      scroll={{ x: 2500, y: 700 }}
      titleEmpty={messages['table.empty']}
      columns={getPendingAgencyApprovalColumns({
        messages,
        payrollCountry,
        handleCancel,
        handleView,
        handleChangeTaxPeriod: handleOpenChangeTaxPeriod,
        theme,
        isBureau,
      })}
      onChangePagination={onChangePagination}
      rowSelection={{
        type: 'checkbox',
        onChange: (_: React.Key[], selectedRows: any[]) => {
          setSelectedRows(selectedRows);
        },
      }}
      noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
    />
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isTimesheetGroupingLoading ||
        isCancelTimesheetPending ||
        isApproveTimesheetPending
      }
    >
      {!isTimesheetGroupingLoading &&
        (isGroupTimesheet ? (
          <Space direction="vertical" size={16}>
            {isEmpty(timesheetGroupingData) ? (
              isDashboard ? (
                <AppEmpty />
              ) : (
                <AppCard>
                  <AppEmpty />
                </AppCard>
              )
            ) : (
              timesheetGroupingData?.map((item) => (
                <TimesheetGroupCard
                  key={item.taxPeriod}
                  data={item}
                  onClick={handleSelectTimesheetGroup}
                />
              ))
            )}
          </Space>
        ) : isDashboard ? (
          <>
            <div className="mb-[20px]">
              <HeaderListing onFilter={handleFilter} isPendingApproval={true} />
            </div>
            <AppsContent>{renderTable()}</AppsContent>
          </>
        ) : (
          <AppCard>
            <AppsHeader>
              <HeaderListing onFilter={handleFilter} isPendingApproval={true} />
            </AppsHeader>
            <AppsContent>{renderTable()}</AppsContent>
          </AppCard>
        ))}
      <ChangeTaxPeriodModal
        isOpen={taxPeriodModal.isOpen}
        toggle={toggleTaxPeriodModal}
        timesheets={taxPeriodModal.timesheets}
        onSuccess={() => {
          refetchTimesheetList();
          setSelectedRows([]);
        }}
      />
    </AppsContainer>
  );
};

export default PendingApproval;
